import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    LIST, VIEW, ADD, EDIT, DELETE, ACTIVE, INACTIVE,
} from "const"
import { deepCopy, hasAccess } from "client/utils"
import {
    PRM_PERMISSIONS,
    StoreType,
    addModal,
    updateBaseProjectPermission,
    toggleBaseProjectPermissionCustomizable,
    setAccountPermissionsGroupSelected,
    setAccountPermissionsProjectGroupSelected,
} from "client/store"
import {
    ProjectPermissionRow,
    ProjectPermissionItem,
    PermissionType,
} from "client/store/utils"
import {
    PermissionTableColumn,
    PermissionList,
    PermissionListItem,
    PermissionListAddItem,
    PermissionLabel,
    Permission,
    DeletePermissionButton,
    StatusSwitcher,
} from "client/components"

import NotAssigned from "./notAssigned"

const Columns = (
    accountId: number,
) => {
    const accountGroupList = useSelector((state: StoreType) => state.group.groupList)
    const projectGroupList = useSelector((state: StoreType) => state.projectGroup.groupList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const editAccess = hasAccess(PRM_PERMISSIONS, EDIT)

    const permissionFormatter = (
        section: string,
        permissionId: string,
        permissionType: PermissionType,
        permission: ProjectPermissionRow,
    ) => {
        const permissionItem = permission[permissionType]
        if (!permissionItem) return null // unused permission

        const { accountGroups, projectGroups } = permissionItem
        return (
            <PermissionList>
                {
                    accountGroups.map((groupId) => {
                        const group = accountGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    editAccess && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.accountGroups = accountGroups.filter((id) => id !== groupId)
                                                dispatch(updateBaseProjectPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    projectGroups.map((groupId) => {
                        const group = projectGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    editAccess && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.projectGroups = projectGroups.filter((id) => id !== groupId)
                                                dispatch(updateBaseProjectPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    editAccess && (
                        <PermissionListAddItem
                            onClick={() => {
                                dispatch(setAccountPermissionsGroupSelected(null))
                                dispatch(setAccountPermissionsProjectGroupSelected(null))
                                dispatch(addModal({
                                    key: "notAssignedProjectPermissions",
                                    content: <NotAssigned
                                        accountId={accountId}
                                        section={section}
                                        permissionId={permissionId}
                                        permissionType={permissionType}
                                        permissionItem={permissionItem}
                                    />,
                                }))
                            }}
                        >
                            {`+ ${lang.add}`}
                        </PermissionListAddItem>
                    )
                }
            </PermissionList>
        )
    }

    const cellMinWidth = "200px"
    const columns: PermissionTableColumn[] = [
        {
            field: "name",
            header: lang.name,
            cellMinWidth,
            cellMaxWidth: "300px",
            formatter: (_section, _permissionId, permission: ProjectPermissionRow) => permission.title,
        },
        {
            field: LIST,
            header: lang.list,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, LIST, permission)
            ),
        },
        {
            field: VIEW,
            header: lang.view,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, VIEW, permission)
            ),
        },
        {
            field: ADD,
            header: lang.add,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, ADD, permission)
            ),
        },
        {
            field: EDIT,
            header: lang.edit,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, EDIT, permission)
            ),
        },
        {
            field: DELETE,
            header: lang.delete,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, DELETE, permission)
            ),
        },
    ]

    if (editAccess) {
        columns.push({
            field: "customizable",
            header: lang.customizable,
            cellWidth: "100px",
            cellMinWidth: "100px",
            centerAlign: true,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                <StatusSwitcher
                    status={permission.customizable ? ACTIVE : INACTIVE}
                    onClick={() => dispatch(toggleBaseProjectPermissionCustomizable(section, permissionId))}
                />
            ),
        })
    }

    return columns
}
export default Columns
